import React, { useEffect, useState } from 'react';
import { Calendar, LayoutGrid } from 'lucide-react';
import { cn } from '../../../../lib/utils';
import TaskAssigneeFilterDropdown from './TaskAssigneeFilterDropdown';
import TaskStatusFilterDropdown from './TaskStatusFilterDropdown';
import { TaskSearchInput } from './TaskSearchInput';
import { UserFilter } from '../../../dashboard/types/dashboardTypes';
import { ViewModeButton } from '../../../ui';
import { logger } from '../../../../utils/logger';

/**
 * Task filter toolbar component with React Query support
 * Contains all the filter controls for task topics including status filters,
 * assignee filters, and view mode toggles
 */
interface TaskFilterToolbarProps {
  isMobile: boolean;
  userFilters: UserFilter[];
  toggleUserFilter: (userId: string, isExclusive?: boolean) => void;
  viewMode: 'board' | 'timeline';
  toggleViewMode: () => void;
  hasCollaborators?: boolean;
  topicId: string;
  // Filter props that still need to be passed
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  showUnscheduledOnly: boolean;
  toggleUnscheduledOnly: () => void;
  showUnassignedOnly: boolean;
  toggleUnassignedOnly: () => void;
  showHighlightedOnly: boolean;
  toggleHighlightedOnly: () => void;
  sortByDeadline: boolean;
  toggleSortByDeadline: () => void;
  resetFilters: () => void;
  availableTags: string[];
}

const TaskFilterToolbar: React.FC<TaskFilterToolbarProps> = ({
  isMobile,
  userFilters,
  toggleUserFilter,
  viewMode,
  toggleViewMode,
  hasCollaborators = false,
  topicId,
  selectedTags,
  setSelectedTags,
  showUnscheduledOnly,
  toggleUnscheduledOnly,
  showUnassignedOnly,
  toggleUnassignedOnly,
  showHighlightedOnly,
  toggleHighlightedOnly,
  sortByDeadline,
  toggleSortByDeadline,
  availableTags,
}) => {
  const [isUserFilterOpen, setIsUserFilterOpen] = useState(false);
  const [isTaskFilterOpen, setIsTaskFilterOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);

  // Ensure only one dropdown is open at a time
  useEffect(() => {
    if (isUserFilterOpen) {
      setIsTaskFilterOpen(false);
      setIsTagFilterOpen(false);
    }
  }, [isUserFilterOpen]);

  useEffect(() => {
    if (isTaskFilterOpen) {
      setIsUserFilterOpen(false);
      setIsTagFilterOpen(false);
    }
  }, [isTaskFilterOpen]);

  useEffect(() => {
    if (isTagFilterOpen) {
      setIsUserFilterOpen(false);
      setIsTaskFilterOpen(false);
    }
  }, [isTagFilterOpen]);

  // Handler for toggling user filter dropdown
  const handleToggleUserFilter = () => {
    setIsUserFilterOpen(!isUserFilterOpen);
  };

  // Handler for toggling task filter dropdown
  const handleToggleTaskFilter = () => {
    setIsTaskFilterOpen(!isTaskFilterOpen);
  };

  // Handler for tag changes
  const handleTagsChange = (tags: string[]) => {
    setSelectedTags(tags);
  };

  // Check if any selected tags are no longer available
  const hasInvalidTags =
    selectedTags.length > 0 && selectedTags.some(tag => !availableTags.includes(tag));

  // Log invalid tags but don't auto-fix
  useEffect(() => {
    if (hasInvalidTags) {
      logger.component('Task has tags that are no longer available', {
        topicId,
        currentTags: selectedTags,
        availableTags,
      });
    }
  }, [hasInvalidTags, selectedTags, availableTags, topicId]);

  return (
    <div
      className={cn(
        'w-full flex items-center py-3 md:py-4 px-1 md:px-5',
        isMobile ? 'justify-center' : 'justify-end' // Center on mobile, end-aligned on desktop
      )}
    >
      {/* Filter Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Search Input - Only show on desktop */}
        {!isMobile && <TaskSearchInput className="mr-1" />}

        {/* User Filter Dropdown - Only show if project has collaborators and there are users to filter by */}
        {hasCollaborators && userFilters.length > 0 && (
          <TaskAssigneeFilterDropdown
            userFilters={userFilters}
            isOpen={isUserFilterOpen}
            onToggle={handleToggleUserFilter}
            onSelect={toggleUserFilter}
            isMobile={isMobile}
          />
        )}

        {/* Task Filter Dropdown - Only show for board view (not timeline) */}
        {viewMode === 'board' && (
          <TaskStatusFilterDropdown
            showUnscheduledOnly={showUnscheduledOnly}
            toggleShowUnscheduledOnly={toggleUnscheduledOnly}
            showUnassignedOnly={showUnassignedOnly}
            toggleShowUnassignedOnly={toggleUnassignedOnly}
            showHighlightedOnly={showHighlightedOnly}
            toggleShowHighlightedOnly={toggleHighlightedOnly}
            sortByDeadline={sortByDeadline}
            toggleSortByDeadline={toggleSortByDeadline}
            isOpen={isTaskFilterOpen}
            onToggle={handleToggleTaskFilter}
            isMobile={isMobile}
            hasCollaborators={hasCollaborators}
            availableTags={availableTags}
            selectedTags={selectedTags}
            onTagsChange={handleTagsChange}
          />
        )}

        {/* View Mode Toggle - using standardized ViewModeButton */}
        {viewMode === 'board' ? (
          <ViewModeButton
            currentMode={viewMode}
            mode="board"
            onClick={toggleViewMode}
            label={isMobile ? 'Calendar' : 'Calendar View'}
            noLabel
            icon={<Calendar size={16} />}
            isMobile={isMobile}
          />
        ) : (
          <ViewModeButton
            currentMode={viewMode}
            mode="timeline"
            onClick={toggleViewMode}
            label={isMobile ? 'Board' : 'Board View'}
            noLabel
            icon={<LayoutGrid size={16} />}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
};

export default TaskFilterToolbar;
